import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import Reveal from "@/components/base/Reveal";
import FaqAccordion from "@/components/feature/FaqAccordion";
import usePageMeta from "@/hooks/usePageMeta";
import { faqGroups, seoKeywords } from "@/mocks/siteData";

const pageTitle = "常见问题 - 匿名投稿、隐私安全与素材要求解答 | 潜声投稿";
const pageDesc =
  "关于匿名爆料投稿的常见问题解答：投稿是否需要实名、平台会不会记录真实身份、视频与录音素材有何要求、线索提交后如何处理，共 17 个高频问题一次性说清楚。";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqGroups.flatMap((group) =>
      group.items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    ),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://qiansheng.online/" },
      { "@type": "ListItem", position: 2, name: "常见问题", item: "https://qiansheng.online/faq" },
    ],
  },
];

export default function Faq() {
  usePageMeta({
    title: pageTitle,
    description: pageDesc,
    keywords: seoKeywords,
    path: "/faq",
    jsonLd,
  });

  return (
    <Layout>
      <PageHero
        eyebrow="常见问题"
        title="投稿之前，先把顾虑说清楚"
        subtitle="我们把投稿者最常问的 17 个问题，按投稿方式、隐私安全、素材要求和后续处理四类整理在这里。如果还有没覆盖到的，直接用私信通道问我们就好。"
        image="https://readdy.ai/api/search-image?query=Abstract%20floating%20rounded%20panels%20arranged%20in%20a%20calm%20grid%20over%20a%20deep%20charcoal%20teal%20background%2C%20soft%20amber%20edge%20light%2C%20subtle%20grain%2C%20minimal%20editorial%20composition%2C%20wide%20negative%20space%2C%20high%20contrast%2C%20no%20text%2C%20no%20people&width=1600&height=700&seq=qs-faq-a1&orientation=landscape"
        imageAlt="深色背景上整齐浮动的圆角面板，象征潜声投稿常见问题按类别有序整理"
        crumbs={[{ label: "首页", to: "/" }, { label: "常见问题" }]}
      />

      <section className="w-full px-4 md:px-8 pt-10 md:pt-14">
        <div className="max-w-content mx-auto">
          <Reveal>
            <div className="flex flex-wrap gap-3">
              {faqGroups.map((group) => (
                <a
                  key={group.id}
                  href={`#${group.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <i className={group.icon}></i>
                  {group.name}
                  <span className="text-xs text-foreground-400">{group.items.length}</span>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-content mx-auto space-y-14">
          {faqGroups.map((group, index) => (
            <div key={group.id} id={group.id} className="scroll-mt-28">
              <Reveal>
                <div className="flex items-center gap-3.5 mb-6">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 text-xl">
                    <i className={group.icon}></i>
                  </span>
                  <h2 className="m-0 font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    {group.name}
                  </h2>
                  <span className="h-px flex-1 bg-background-200"></span>
                  <span className="text-sm text-foreground-400">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
              </Reveal>
              <Reveal delay={60}>
                <FaqAccordion items={group.items} />
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-4">
        <div className="max-w-content mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 text-2xl shrink-0">
                  <i className="ri-question-answer-line"></i>
                </span>
                <div>
                  <h2 className="m-0 font-heading text-lg md:text-xl font-bold text-foreground-950">
                    这里没有你的问题？
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    通过匿名私信通道提问即可，不需要注册，也不必说明身份，我们会尽快回复。
                  </p>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer shrink-0"
              >
                <i className="ri-chat-private-line"></i>
                去提问
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="问题想明白了，就可以放心投稿"
        desc="四类通道都不要求注册与实名，先整理好手里的关键材料，选一条最适合的开始。"
        primaryLabel="前往投稿通道"
        primaryTo="/channels"
        secondaryLabel="阅读安全指南"
        secondaryTo="/guide"
      />
    </Layout>
  );
}